/**
 * Task Dependency Graph Service - Phase 4 Architecture
 *
 * Encapsulates dependency graph construction, traversal, and metrics.
 * Extracted from TaskDependencyService to reduce single-responsibility violations.
 */

import { typedSupabase } from "@/lib/typed-supabase";
import { logger } from "@/lib/logger";
import { IService, ServiceResponse } from "@/services/core/service-registry";
import type {
  DependencyGraph,
  TaskDependency,
  TaskNode,
} from "@/types/time-management";
import { TaskDependencyValidationService } from "./task-dependency-validation.service";
import { TaskDependencyCriticalPathService } from "./task-dependency-critical-path.service";

export class TaskDependencyGraphService implements IService {
  public readonly name = "TaskDependencyGraphService";
  private readonly supabase = typedSupabase;
  private validationService: TaskDependencyValidationService;
  private criticalPathService: TaskDependencyCriticalPathService;

  constructor(
    validationService: TaskDependencyValidationService,
    criticalPathService: TaskDependencyCriticalPathService
  ) {
    this.validationService = validationService;
    this.criticalPathService = criticalPathService;
  }

  async initialize(): Promise<void> {
    logger.info("✅ TaskDependencyGraphService initialized");
  }

  async dispose(): Promise<void> {
    logger.info("✅ TaskDependencyGraphService disposed");
  }

  async buildDependencyGraph(
    taskIds: string[]
  ): Promise<ServiceResponse<DependencyGraph>> {
    try {
      const [tasksResult, dependenciesResult] = await Promise.all([
        this.supabase
          .from("todos")
          .select("id, title, status, priority")
          .in("id", taskIds),
        this.supabase
          .from("task_dependencies")
          .select("*")
          .or(
            `predecessor_task_id.in.(${taskIds.join(",")}),successor_task_id.in.(${taskIds.join(",")})`
          ),
      ]);

      if (tasksResult.error || dependenciesResult.error) {
        return {
          success: false,
          error:
            tasksResult.error?.message ??
            dependenciesResult.error?.message ??
            "Unknown error",
        };
      }

      const nodes: TaskNode[] = (tasksResult.data ?? []).map((task) => ({
        id: task.id,
        title: task.title,
        status: task.status,
        priority: task.priority,
        dependencies: [],
        dependents: [],
      }));

      const typedEdges: TaskDependency[] = (dependenciesResult.data ?? []).map(
        (dep) => ({
          id: dep.id,
          predecessor_task_id: dep.predecessor_task_id,
          successor_task_id: dep.successor_task_id,
          dependency_type: dep.dependency_type as
            | "finish_to_start"
            | "start_to_start"
            | "finish_to_finish"
            | "start_to_finish",
          lag_time: dep.lag_time ?? 0,
          created_at: dep.created_at ?? new Date().toISOString(),
          updated_at: dep.updated_at ?? new Date().toISOString(),
        })
      );

      nodes.forEach((node) => {
        node.dependencies = typedEdges
          .filter((edge) => edge.successor_task_id === node.id)
          .map((edge) => edge.predecessor_task_id);
        node.dependents = typedEdges
          .filter((edge) => edge.predecessor_task_id === node.id)
          .map((edge) => edge.successor_task_id);
      });

      return {
        success: true,
        data: {
          nodes,
          edges: typedEdges,
          criticalPath: undefined,
          totalDuration: undefined,
          complexity: this.calculateGraphComplexity(
            nodes.length,
            typedEdges.length
          ),
          circularDependencies: [],
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getDependencyChain(
    taskId: string,
    direction: "forward" | "backward"
  ): Promise<ServiceResponse<string[]>> {
    try {
      const chain: string[] = [];
      const visited = new Set<string>();

      const buildChain = async (currentTaskId: string): Promise<void> => {
        if (visited.has(currentTaskId)) {return;}
        visited.add(currentTaskId);
        chain.push(currentTaskId);

        const whereField =
          direction === "forward" ? "predecessor_task_id" : "successor_task_id";

        const { data: dependencies } = await this.supabase
          .from("task_dependencies")
          .select("predecessor_task_id, successor_task_id")
          .eq(whereField, currentTaskId);

        if (dependencies) {
          for (const dep of dependencies) {
            const nextTaskId =
              direction === "forward"
                ? dep.successor_task_id
                : dep.predecessor_task_id;
            await buildChain(nextTaskId);
          }
        }
      };

      await buildChain(taskId);
      return { success: true, data: chain.slice(1) };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getParallelTasks(taskId: string): Promise<ServiceResponse<string[]>> {
    try {
      const [forwardChain, backwardChain] = await Promise.all([
        this.getDependencyChain(taskId, "forward"),
        this.getDependencyChain(taskId, "backward"),
      ]);

      if (!forwardChain.success || !backwardChain.success) {
        return {
          success: false,
          error:
            (forwardChain.success ? backwardChain.error : forwardChain.error) ??
            "Failed to resolve dependency chains",
        };
      }

      const dependentTasks = new Set([
        ...(forwardChain.data ?? []),
        ...(backwardChain.data ?? []),
        taskId,
      ]);

      const { data: task } = await this.supabase
        .from("todos")
        .select("client_id, created_by")
        .eq("id", taskId)
        .single();

      if (!task) {
        return { success: true, data: [] };
      }

      let projectTasksQuery = this.supabase.from("todos").select("id");

      if (task.client_id) {
        projectTasksQuery = projectTasksQuery.eq("client_id", task.client_id);
      } else {
        projectTasksQuery = projectTasksQuery.eq("created_by", task.created_by ?? '');
      }

      const { data: projectTasks } = await projectTasksQuery;

      const parallelTasks = (projectTasks ?? [])
        .map((t) => t.id)
        .filter((id) => !dependentTasks.has(id));

      return { success: true, data: parallelTasks };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getBlockedTasks(taskId: string): Promise<ServiceResponse<string[]>> {
    try {
      return await this.getDependencyChain(taskId, "forward");
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  async getDependencyMetrics(taskIds: string[]): Promise<
    ServiceResponse<{
      totalDependencies: number;
      complexityScore: number;
      averageDependenciesPerTask: number;
      longestChain: number;
      circularDependencies: number;
      criticalPathLength: number;
    }>
  > {
    try {
      const { data: dependencies } = await this.supabase
        .from("task_dependencies")
        .select("*")
        .or(
          `predecessor_task_id.in.(${taskIds.join(",")}),successor_task_id.in.(${taskIds.join(",")})`
        );

      const totalDependencies = dependencies?.length ?? 0;
      const averageDependenciesPerTask =
        taskIds.length > 0 ? totalDependencies / taskIds.length : 0;
      const complexityScore = this.calculateGraphComplexity(
        taskIds.length,
        totalDependencies
      );

      let longestChain = 0;
      for (const taskId of taskIds) {
        const chainResult = await this.getDependencyChain(taskId, "forward");
        if (chainResult.success && chainResult.data) {
          longestChain = Math.max(longestChain, chainResult.data.length);
        }
      }

      let circularDependencies = 0;
      for (const taskId of taskIds) {
        const circularResult = await this.validationService.detectCircularDependencies(
          taskId
        );
        if (circularResult.success && circularResult.data) {
          circularDependencies += circularResult.data.length;
        }
      }

      const criticalPathResult = await this.criticalPathService.calculateCriticalPath(
        taskIds
      );
      const criticalPathLength =
        criticalPathResult.success && criticalPathResult.data
          ? criticalPathResult.data.tasks.length
          : 0;

      return {
        success: true,
        data: {
          totalDependencies,
          complexityScore,
          averageDependenciesPerTask,
          longestChain,
          circularDependencies,
          criticalPathLength,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      };
    }
  }

  private calculateGraphComplexity(
    nodeCount: number,
    edgeCount: number
  ): number {
    if (nodeCount === 0) {return 0;}
    const density = edgeCount / (nodeCount * (nodeCount - 1));
    return Math.round(density * 100);
  }
}
